import type {
  FeatureFlagPayloadMap,
  PlatformFeatureFlag,
} from '@infinex/types';

import { useDataset } from './useDataset';

// [Jed] This function returns undefined while loading, and a boolean when loaded.
// This means that loading feature flags will resolve to falsy, but if you care
// about the loading state, you can check for undefined.
export const usePlatformFeatureFlagEnabled = (
  featureFlag: PlatformFeatureFlag
) => {
  const { data: featureFlags, status: featureFlagsStatus } =
    useDataset('featureFlags');

  const isEnabled = featureFlags?.featureFlags?.[featureFlag]?.enabled || false;

  if (featureFlagsStatus === 'loading' || featureFlagsStatus === 'idle') {
    return undefined;
  }

  return isEnabled;
};

/**
 * Hook for feature flags with payloads.
 */
export const usePlatformFeatureFlagPayload = <
  T extends keyof FeatureFlagPayloadMap,
>(
  featureFlag: T
): { enabled: boolean; payload?: FeatureFlagPayloadMap[T] } | undefined => {
  const { data: featureFlags, status: featureFlagsStatus } =
    useDataset('featureFlags');

  // Access the resolved flags from the special _resolved key
  const resolved = featureFlags?.featureFlags?.[featureFlag];

  if (featureFlagsStatus === 'loading' || featureFlagsStatus === 'idle') {
    return undefined;
  }

  // If not found in resolved, check if it's a simple boolean flag
  // and return it in resolved format for consistency
  if (!resolved || !resolved.enabled) {
    return { enabled: false };
  }

  return resolved as { enabled: boolean; payload?: FeatureFlagPayloadMap[T] };
};
